import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import type { WorkflowExample } from '../types';

const WorkflowExamples = () => {
  const [selectedPlatform, setSelectedPlatform] = useState<'all' | 'n8n' | 'make'>('all');

  const workflows: WorkflowExample[] = [
    {
      id: '1',
      title: 'Omni-Channel Lead Router',
      description: 'Captures leads from Facebook Ads, Website, and LinkedIn. Uses AI to score them. High-value leads go to Slack; low-value leads go to email nurture.',
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80", // Placeholder
      tools: ['Facebook Ads', 'OpenAI', 'Slack', 'HubSpot'],
      roi: 'Saved 15hrs/week',
      // @ts-ignore - Adding custom field for filtering
      platform: 'make' 
    },
    {
      id: '2',
      title: 'E-commerce Inventory Sync',
      description: 'Real-time sync between Shopify, Amazon FBA, and Google Sheets. Automatically pauses ads when stock runs low.',
      image: "https://images.unsplash.com/photo-1556742049-0cfed4f7a07d?auto=format&fit=crop&q=80",
      tools: ['Shopify', 'Amazon API', 'Google Sheets', 'Meta Ads'],
      roi: 'Zero Overselling',
      // @ts-ignore
      platform: 'n8n'
    },
    {
      id: '3',
      title: 'Automated Client Onboarding',
      description: 'Triggered by Stripe payment. Generates contract, creates Google Drive folder, invites to Slack, and schedules kick-off call.',
      image: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&q=80",
      tools: ['Stripe', 'DocuSign', 'Google Drive', 'Slack'],
      roi: 'Onboarding: 5m vs 2hrs',
      // @ts-ignore
      platform: 'make'
    }
  ];

  const filteredWorkflows = workflows.filter(w => selectedPlatform === 'all' || (w as any).platform === selectedPlatform);

  return (
    <section className="py-20 bg-muted/30 border-y border-border/50" id="examples">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
          <div className="max-w-2xl">
            <h2 className="text-3xl font-bold text-foreground mb-4">Real-World Automations</h2>
            <p className="text-lg text-muted-foreground">See what's possible when you remove manual work.</p>
          </div>
          
          {/* Filter Tabs */}
          <div className="flex p-1 bg-background rounded-lg border border-border shadow-sm">
            {['all', 'n8n', 'make'].map((tab) => (
              <button
                key={tab}
                onClick={() => setSelectedPlatform(tab as any)}
                className={`px-4 py-2 rounded-md text-sm font-medium capitalize transition-all ${
                  selectedPlatform === tab 
                    ? 'bg-primary text-primary-foreground shadow-sm' 
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {tab === 'all' ? 'All Examples' : tab}
              </button>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredWorkflows.map((workflow) => (
            <div key={workflow.id} className="group bg-card rounded-2xl border border-border overflow-hidden hover:shadow-xl transition-all duration-300">
              
              {/* Image & Badge */}
              <div className="relative h-48 bg-muted">
                <Image src={workflow.image} alt={workflow.title} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500" />
                <div className="absolute top-4 right-4">
                  <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wide text-white ${(workflow as any).platform === 'n8n' ? 'bg-orange-500' : 'bg-purple-600'}`}>
                    {(workflow as any).platform === 'n8n' ? 'n8n' : 'Make'}
                  </span>
                </div>
              </div>

              <div className="p-6">
                <h3 className="text-xl font-bold text-foreground mb-2 group-hover:text-primary transition-colors">
                  {workflow.title}
                </h3>
                <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
                  {workflow.description}
                </p>

                {/* Tags */}
                <div className="flex flex-wrap gap-2 mb-6">
                  {workflow.tools.map((tool, i) => (
                    <span key={i} className="px-2 py-1 bg-muted rounded border border-border text-[10px] font-medium text-foreground">
                      {tool}
                    </span>
                  ))}
                </div>

                {/* Footer ROI */}
                <div className="pt-4 border-t border-border flex items-center justify-between">
                   <div className="flex items-center gap-2 text-success text-sm font-bold">
                     <Icon name="TrendingUp" size={16} />
                     {workflow.roi}
                   </div>
                   <Icon name="ArrowRight" size={16} className="text-muted-foreground group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default WorkflowExamples;